import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { verifyToken } from "shared/api/client/orvalHelpers";
import { userApi } from "shared/api/client/orval";
import { getApiOrigin } from "shared/api/client/apiOrigin";
import { parseApiError } from "shared/api/client/errorParser";

import "../../../styles/legacy/Signup.css";

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [nameErrors, setNameErrors] = useState<{
    firstName?: string;
    lastName?: string;
  }>({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "CREATOR",
  });
  const [title] = useState({
    CREATOR: "CREATOR PLATFORM",
    USER: "USER PLATFORM",
    CURATOR: "CURATOR PLATFORM",
  });
  const [description] = useState({
    CREATOR: "Get access to the creator platform to upload, manage and monitor your content's performance.",
    USER: "Get access to the user platform to discover, enjoy, and interact with a world of unique content.",
    CURATOR: "Access the curator platform to discover, organize, and showcase top-tier content, while tracking engagement and audience growth.",
  });

  // Curator accounts are invite-only: the option is only offered (and the
  // signup only accepted) when a valid-looking invite token is in the URL.
  const searchParams = new URLSearchParams(location.search);
  const inviteToken = (
    searchParams.get("invite") ||
    searchParams.get("token") ||
    ""
  ).trim();

  const userType = formData.role === "USER" ? "Viewer" : formData.role === "CURATOR" ? "Curator" : "Creator";
  const oauthQuery = inviteToken ? `?token=${encodeURIComponent(inviteToken)}` : "";

  const GOOGLE_OAUTH_URL_SU = `${getApiOrigin()}/api/User/google-login/${userType}${oauthQuery}`;
  const FACEBOOK_OAUTH_URL_SU = `${getApiOrigin()}/api/User/facebook-login/${userType}${oauthQuery}`;

  useEffect(() => {
    const token = getCookie("userToken");
    if (!token) {
      setIsVerified(false);
      return;
    }
    verifyToken(token).then((result: boolean) => setIsVerified(result));
  }, []);

  if (isVerified === null) {
    return <>Loading...</>;
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "password") {
      setPasswordErrors(validatePassword(value));
    }
  };

  const validatePassword = (pwd: string): string[] => {
    const errors = [];
    if (!pwd) {
      errors.push("Password is required");
      return errors;
    }

    if (pwd.length < 8) errors.push("8 characters minimum");
    if (!/[A-Z]/.test(pwd)) errors.push("at least 1 capital letter");
    if (!/\d/.test(pwd)) errors.push("1 number");
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errors.push("1 special character");
    return errors;
  };

  const validateEmail = (email: string): string | null => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) ? null : "Invalid email.";
  };

  const validateNames = () => {
    const errors: { firstName?: string; lastName?: string } = {};
    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    setNameErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAccount = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    const areNamesValid = validateNames();
    const emailValidationError = validateEmail(formData.email);
    const passwordValidationErrors = validatePassword(formData.password);

    setEmailError(emailValidationError);
    setPasswordErrors(passwordValidationErrors);

    if (
      !areNamesValid ||
      emailValidationError ||
      passwordValidationErrors.length > 0
    ) {
      return;
    }

    setIsLoading(true);
    setEmailError(null);

    const payload = {
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      role: formData.role,
    };

    try {
      if (formData.role === "CURATOR") {
        if (!inviteToken) {
          setEmailError("A curator invite link is required to sign up as a curator.");
          return;
        }
        await userApi.postApiUserSignup(payload as any, { token: inviteToken });
      } else {
        await userApi.postApiUserCreateusrRole(formData.role, payload as any);
      }
      setAccountCreated(true);
    } catch (err) {
      console.error("Request error:", err);
      setEmailError(parseApiError(err, "Could not create your account."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="su-logo-container" onClick={() => navigate("/login")}>
        <img src="/images/poetics-stream-lockup.svg" alt="Poetics Stream" style={{ height: 30, width: "auto" }} />
        <h1 className="app-logo-subtitle">{title[formData.role as "CREATOR" | "USER" | "CURATOR"]}</h1>
      </div>

      <div className="signup-box">
        {isLoading ? (
          <div className="spinner" role="status" />
        ) : accountCreated ? (
          <div className="success-message">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_3541_20062)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM7.17719 11.2464L9.91869 13.9114L16.8228 7.2L18 8.34432L9.91869 16.2L6 12.3907L7.17719 11.2464Z"
                  fill="#22C55E"
                />
              </g>
              <defs>
                <clipPath id="clip0_3541_20062">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <h1 className="success-title">Done!</h1>
            <p className="success-text">
              Check your inbox for the verification email
            </p>
            <button
              className="signup-login-button"
              onClick={() => navigate("/login")}
            >
              Enter Poetics Stream
            </button>
          </div>
        ) : (
          <>
            <div className="create-account-container">
              <h1 className="create-account">Create your Poetics Stream account</h1>
              <p className="sign-description">
              {description[formData.role as "CREATOR" | "USER" | "CURATOR"]}
              </p>
            </div>

            <div className="signup-form">
              <div className="signup-frame">
                <h2 className="signup-with">Sign up with</h2>
                <div className="button-section">
                  <button
                    type="button"
                    className="media-login-button"
                    onClick={() => (window.location.href = GOOGLE_OAUTH_URL_SU)}
                  >
                    <img
                      src="/images/mobile/SSO_buttons/google.svg"
                      alt="Google"
                      className="sso-logo-desktop"
                      style={{ height: 20 }}
                    />
                    Google
                  </button>
                  <button
                    type="button"
                    className="media-login-button"
                    onClick={() =>
                      (window.location.href = FACEBOOK_OAUTH_URL_SU)
                    }
                  >
                    <svg
                      viewBox="0 0 320 512"
                      className="sso-logo-desktop"
                      style={{ height: 20, width: 20, flex: "none" }}
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        fill="#FAFAFA"
                        d="M279.14 288l14.22-92.66h-88.91V117.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"
                      />
                    </svg>
                    Facebook
                  </button>
                </div>
              </div>
              <div className="or-separator">or</div>
              <div className="signup-fields">
                <div className="role-select-container">
                  <label
                    htmlFor="role"
                    className="role-select-label"
                  >
                    Account type
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="su-input-field role-select"
                  >
                    <option value="CREATOR">Creator</option>
                    <option value="USER">User</option>
                    {inviteToken && <option value="CURATOR">Curator</option>}
                  </select>
                </div>
                <div className="name-input-container">
                  <div className="name-input-wrapper">
                    <input
                      type="text"
                      name="firstName"
                      placeholder="First Name"
                      className={`name-input-field ${
                        nameErrors.firstName ? "error-border" : ""
                      }`}
                      value={formData.firstName}
                      onChange={handleInputChange}
                      autoComplete="given-name"
                      required
                    />
                    {nameErrors.firstName && (
                      <div className="error-message">
                        {nameErrors.firstName}
                      </div>
                    )}
                  </div>
                  <div className="name-input-wrapper">
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      className={`name-input-field ${
                        nameErrors.lastName ? "error-border" : ""
                      }`}
                      value={formData.lastName}
                      onChange={handleInputChange}
                      autoComplete="family-name"
                      required
                    />
                    {nameErrors.lastName && (
                      <div className="error-message">{nameErrors.lastName}</div>
                    )}
                  </div>
                </div>
                <div className="email-input-container">
                  <div className="input-wrapper">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email"
                      className={`su-input-field ${
                        emailError ? "error-border" : ""
                      }`}
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="email"
                      required
                    />
                    {emailError && (
                      <div className="error-message">{emailError}</div>
                    )}
                  </div>
                </div>
                
                <div className="password-input-container">
                  <div className="input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Password"
                      className={`su-input-field ${
                        passwordErrors.length > 0 ? "error-border" : ""
                      }`}
                      value={formData.password}
                      onChange={handleInputChange}
                      autoComplete="current-password"
                      required
                    />
                    <button 
                      type="button" 
                      className={`S-eye-button ${
                        passwordErrors.length > 0 ? "S-eye-button-error" : ""
                      }`} 
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                    <svg width="23px" height="30px" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.68788 8.3059C1.36566 7.85735 1.46807 7.23252 1.91662 6.9103C2.36517 6.58809 2.99 6.6905 3.31221 7.13905C3.61607 7.56204 4.08478 7.96552 4.6918 8.31591C5.98518 9.0625 7.7771 9.5 9.7043 9.5C11.6315 9.5 13.4234 9.0625 14.7168 8.31591C15.3238 7.96552 15.7925 7.56204 16.0964 7.13905C16.4186 6.6905 17.0434 6.58809 17.492 6.9103C17.9405 7.23252 18.0429 7.85735 17.7207 8.3059C17.2402 8.97475 16.5568 9.56308 15.7167 10.048C14.1049 10.9784 11.9685 11.5 9.7043 11.5C7.44007 11.5 5.30367 10.9784 3.69195 10.048C2.85179 9.56308 2.16836 8.97475 1.68788 8.3059Z" fill="#a3a3a3"/>
                      <path d="M11 11C11 10.4477 10.5523 10 10 10C9.44772 10 9 10.4477 9 11L9 13.5C9 14.0523 9.44772 14.5 10 14.5C10.5523 14.5 11 14.0523 11 13.5L11 11Z" fill="#a3a3a3"/>
                      <path d="M5.52985 10.7575C5.6638 10.2217 6.20673 9.8959 6.74253 10.0298C7.27832 10.1638 7.60408 10.7067 7.47013 11.2425L6.97013 13.2425C6.83619 13.7783 6.29325 14.1041 5.75746 13.9701C5.22166 13.8362 4.8959 13.2933 5.02985 12.7575L5.52985 10.7575Z" fill="#a3a3a3"/>
                      <path d="M13.9702 10.7575C13.8362 10.2217 13.2933 9.8959 12.7575 10.0298C12.2217 10.1638 11.8959 10.7067 12.0299 11.2425L12.5299 13.2425C12.6638 13.7783 13.2067 14.1041 13.7425 13.9701C14.2783 13.8362 14.6041 13.2933 14.4702 12.7575L13.9702 10.7575Z" fill="#a3a3a3"/>
                      <path d="M16.5249 8.29289C16.1344 7.90237 15.5012 7.90237 15.1107 8.29289C14.7201 8.68342 14.7201 9.31658 15.1107 9.70711L17.1107 11.7071C17.5012 12.0976 18.1344 12.0976 18.5249 11.7071C18.9154 11.3166 18.9154 10.6834 18.5249 10.2929L16.5249 8.29289Z" fill="#a3a3a3"/>
                      <path d="M3.07775 8.32742C3.44921 7.91872 4.08165 7.88853 4.49036 8.25999C4.89906 8.63145 4.92924 9.26389 4.55779 9.67259L2.74002 11.6726C2.36857 12.0813 1.73612 12.1115 1.32742 11.74C0.918719 11.3686 0.88853 10.7361 1.25999 10.3274L3.07775 8.32742Z" fill="#a3a3a3"/>
                    </svg>
                  ) : (
                    <svg width="23px" height="30px" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M10 16.5C14.8972 16.5 19 14.1922 19 11C19 7.80784 14.8972 5.5 10 5.5C5.10282 5.5 1 7.80784 1 11C1 14.1922 5.10282 16.5 10 16.5ZM10 7.5C13.9394 7.5 17 9.2216 17 11C17 12.7784 13.9394 14.5 10 14.5C6.06062 14.5 3 12.7784 3 11C3 9.2216 6.06062 7.5 10 7.5Z" fill="#a3a3a3"/>
                      <path d="M9 3.5C9 2.94772 9.44772 2.5 10 2.5C10.5523 2.5 11 2.94772 11 3.5L11 6.5C11 7.05228 10.5523 7.5 10 7.5C9.44772 7.5 9 7.05228 9 6.5L9 3.5Z" fill="#a3a3a3"/>
                      <path d="M13.0194 3.80383C13.1277 3.26227 13.6546 2.91106 14.1961 3.01937C14.7377 3.12768 15.0889 3.65451 14.9806 4.19606L14.4806 6.69606C14.3723 7.23762 13.8455 7.58884 13.3039 7.48053C12.7623 7.37222 12.4111 6.84539 12.5194 6.30383L13.0194 3.80383Z" fill="#a3a3a3"/>
                      <path d="M6.98063 3.80383C6.98063 3.26227 6.4538 2.91106 5.91224 3.01937C5.37068 3.12768 5.01947 3.65451 5.12778 4.19606L5.62778 6.69606C5.73609 7.23762 6.26292 7.58884 6.80447 7.48053C7.34603 7.37222 7.69725 6.84539 7.58894 6.30383L7.08894 3.80383Z" fill="#a3a3a3"/>
                      <path d="M2.85752 4.98553C2.57338 4.51195 1.95912 4.35839 1.48554 4.64254C1.01196 4.92668 0.858391 5.54094 1.14254 6.01452L2.64254 8.51452C2.92669 8.98811 3.54095 9.14167 4.01453 8.85752C4.48811 8.57337 4.64167 7.95911 4.35752 7.48553L2.85752 4.98553Z" fill="#a3a3a3"/>
                      <path d="M17.1425 4.98553C17.4267 4.51195 18.0409 4.35839 18.5145 4.64254C18.9881 4.92668 19.1417 5.54094 18.8575 6.01452L17.3575 8.51452C17.0734 8.98811 16.4591 9.14167 15.9855 8.85752C15.512 8.57337 15.3584 7.95911 15.6425 7.48553L17.1425 4.98553Z" fill="#a3a3a3"/>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10 14C11.933 14 13.5 12.433 13.5 10.5C13.5 8.567 11.933 7 10 7C8.067 7 6.5 8.567 6.5 10.5C6.5 12.433 8.067 14 10 14ZM10 9C10.8284 9 11.5 9.67157 11.5 10.5C11.5 11.3284 10.8284 12 10 12C9.17157 12 8.5 11.3284 8.5 10.5C8.5 9.67157 9.17157 9 10 9Z" fill="#a3a3a3"/>
                    </svg>
                  )} 
                    </button>
                    {passwordErrors.length > 0 && (
                      <div className="password-rules">
                        {passwordErrors.includes("Password is required")
                          ? "Password is required"
                          : passwordErrors.join(", ")}
                      </div>
                    )}
                  </div>
                </div>
                <div className="create-account-btn-container">
                  <button
                    className="create-account-btn"
                    onClick={handleCreateAccount}
                  >
                    Create account
                  </button>
                </div>
              </div>

              <div className="signup-prompt">
                <p className="account">Already have an account? </p>
                <span className="login-link" onClick={() => navigate("/login")}>
                  Log in
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      <p className="su-agreement-text">
        By continuing you agree to Poetics Stream's{" "}
        <a
          href="/terms-of-service"
          className="agreement-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of service
        </a>{" "}
        and{" "}
        <a
          href="/privacy-policy"
          className="agreement-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy policy
        </a>
        .
      </p>
    </div>
  );
};

export default SignupPage;
